/**
 * Which palette is on screen.
 *
 * The choice is light or dark. Until the player makes one the system's
 * preference decides, so a first visit matches the desk it happens at, and from
 * the first press on what they chose is what they get. The document carries the
 * answer as `data-theme`, which is what `app.css` reads, and the choice itself
 * is remembered in local storage.
 *
 * `index.html` makes the same decision before the first paint, from the same
 * two inputs, so that a light-theme player never sees a dark page flash past.
 * Nothing else needs to know: every colour in the application comes from the
 * variables that attribute selects.
 */

export type Palette = 'light' | 'dark';

const THEME_KEY = 'lambda-game:theme';

/** What the system is asking for right now. */
export function systemPalette(): Palette {
  if (typeof matchMedia !== 'function') {
    return 'dark';
  }
  return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** Which palette is on screen, given what the player has chosen. */
export function paletteOf(choice: Palette | null): Palette {
  return choice ?? systemPalette();
}

/** What the control says, on hover and to a screen reader. */
export function describeTheme(palette: Palette): string {
  return palette === 'light' ? 'Switch to the dark theme' : 'Switch to the light theme';
}

/** The palette the player chose last time, if the browser still remembers it. */
export function storedChoice(): Palette | null {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null;
  }
}

/** Puts a palette on the document. */
export function applyPalette(palette: Palette): void {
  document.documentElement.dataset.theme = palette;
  // The browser paints its own chrome with this, and it should be the page
  // colour rather than the dark one it was written with.
  const meta = document.querySelector('meta[name="theme-color"]');
  const background = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  if (meta !== null && background !== '') {
    meta.setAttribute('content', background);
  }
}

/** Remembers the choice, for the next visit. */
export function storeChoice(palette: Palette): void {
  try {
    localStorage.setItem(THEME_KEY, palette);
  } catch {
    // A browser that refuses storage still gets the theme for this visit.
  }
}
